import { useCallback } from "react";
import { useDispatch, useStore } from "react-redux";

import useAxios from "./useAxios";
import { setVisitorDetail } from "../redux/slices/memberSlice";
import { getVisitorFaceData } from "../utils/getVisitorFaceData";

// Centralises the admin/profile/get-visitor-detail call so pages only hit the
// API when redux does not already have the visitor's data.
//
// - getVisitorDetail(visitorId?, requestData?) -> Promise<detail|null>
//     * No visitorId / the logged-in user's own id -> returns the cached redux
//       copy (state.visitorDetail), fetching once if it isn't loaded yet.
//     * Any other visitorId (a dependent, or any other visitor we don't keep in
//       redux) -> calls the API. Redux only ever holds the logged-in user's own
//       detail, so everyone else must be fetched.
// - invalidateVisitorDetail() clears the redux copy so the next read refetches
//     (call this after a mutation that changes the visitor, then re-read).
//
// The store is read with store.getState() (not useSelector) so the guard sees
// the current value even immediately after a dispatch in the same handler.
export default function useVisitorDetail() {
  const axios = useAxios();
  const store = useStore();
  const dispatch = useDispatch();

  const getVisitorDetail = useCallback(
    (visitorId, requestData, forceRefresh = false) => {
      const { visitorData, visitorDetail } = store.getState().memberDetails;
      const selfId = visitorData?.ID;
      const id = visitorId || selfId;
      const isSelf = !visitorId || visitorId === selfId;

      const queryParams = new URLSearchParams(window.location.search);
      const isGuardLogin =
        queryParams.get("guard_login") === "true" ||
        !!sessionStorage.getItem("auth_token_with_guard");

      // Logged-in visitor: reuse the full detail already in redux. Any other
      // visitor (a dependent, or a verification target, etc.) is not in redux,
      // so it must be fetched.
      if (
        !forceRefresh &&
        isSelf &&
        visitorDetail &&
        visitorDetail._id === id
      ) {
        return Promise.resolve(visitorDetail);
      }

      return axios({
        url: `admin/profile/get-visitor-detail${
          !isGuardLogin && id ? "?visitor_id=" + id : ""
        }`,
        method: "POST",
        disableRedirect: true,
        data: requestData || { encrypted_face_data: getVisitorFaceData(id) },
      }).then((response) => {
        if (response?.status) {
          // Only cache the logged-in visitor's own detail.
          if (isSelf) dispatch(setVisitorDetail(response.data));
          return response.data;
        }
        return null;
      });
    },
    [axios, dispatch, store]
  );

  const invalidateVisitorDetail = useCallback(() => {
    dispatch(setVisitorDetail(null));
  }, [dispatch]);

  return { getVisitorDetail, invalidateVisitorDetail };
}
